const ERROR_DEFINITIONS = {
    SecurityError: {
        code: 'SECURE_CONTEXT_REQUIRED',
        message: 'HTTPS obrigatório para acessar a câmera neste endereço.',
        recoverable: false,
    },
    NotAllowedError: {
        code: 'CAMERA_PERMISSION_DENIED',
        message: 'Permissão da câmera negada. Libere o acesso nas configurações do navegador.',
        recoverable: false,
    },
    PermissionDeniedError: {
        code: 'CAMERA_PERMISSION_DENIED',
        message: 'Permissão da câmera negada. Libere o acesso nas configurações do navegador.',
        recoverable: false,
    },
    NotFoundError: {
        code: 'CAMERA_NOT_FOUND',
        message: 'Nenhuma câmera foi encontrada neste dispositivo.',
        recoverable: false,
    },
    DevicesNotFoundError: {
        code: 'CAMERA_NOT_FOUND',
        message: 'Nenhuma câmera foi encontrada neste dispositivo.',
        recoverable: false,
    },
    NotReadableError: {
        code: 'CAMERA_NOT_READABLE',
        message: 'A câmera está ocupada ou não pôde ser lida. Feche outros aplicativos que possam estar usando-a.',
        recoverable: true,
    },
    TrackStartError: {
        code: 'CAMERA_NOT_READABLE',
        message: 'A câmera está ocupada ou não pôde ser iniciada.',
        recoverable: true,
    },
    OverconstrainedError: {
        code: 'CAMERA_CONSTRAINT_UNSUPPORTED',
        message: 'A câmera não aceita as configurações solicitadas.',
        recoverable: true,
    },
    ConstraintNotSatisfiedError: {
        code: 'CAMERA_CONSTRAINT_UNSUPPORTED',
        message: 'A câmera não aceita as configurações solicitadas.',
        recoverable: true,
    },
    AbortError: {
        code: 'CAMERA_ABORTED',
        message: 'O acesso à câmera foi interrompido pelo navegador.',
        recoverable: true,
    },
};

const EXPECTED_DECODE_ERROR_NAMES = new Set([
    'NotFoundException',
    'ChecksumException',
    'FormatException',
    'DecodeException',
]);
const EXPECTED_DECODE_MESSAGE = /(?:no\s+multi\s*format\s+readers?|no\s+barcode\s+(?:was\s+)?found|no\s+(?:qr\s+)?code\s+(?:was\s+)?found)/i;

export class ScannerError extends Error {
    constructor(code, message, options = {}) {
        super(message, { cause: options.cause });
        this.name = 'ScannerError';
        this.code = code;
        this.recoverable = options.recoverable ?? false;
        this.context = options.context || 'scanner';
        this.details = options.details || {};
    }
}

function matchesExpectedDecodeMiss(error, seen) {
    if (!error || seen.has(error)) {
        return false;
    }

    if (typeof error === 'object' || typeof error === 'function') {
        seen.add(error);
    }

    if (error?.code === 'DECODE_NO_RESULT') {
        return true;
    }

    const identities = [
        error?.name,
        error?.constructor?.name,
        error?.getKind?.(),
        error?.kind,
        error?.details?.originalName,
        error?.details?.originalKind,
    ].filter(Boolean);

    if (
        identities.some((identity) => (
            EXPECTED_DECODE_ERROR_NAMES.has(String(identity))
        ))
    ) {
        return true;
    }

    const messages = [
        error?.message,
        error?.details?.originalMessage,
    ].filter(Boolean);

    if (
        messages.some((message) => (
            EXPECTED_DECODE_MESSAGE.test(String(message))
        ))
    ) {
        return true;
    }

    return matchesExpectedDecodeMiss(error?.cause, seen);
}

export function isExpectedDecodeMiss(error) {
    return matchesExpectedDecodeMiss(error, new Set());
}

export function normalizeError(error, context = 'scanner') {
    if (error instanceof ScannerError) {
        return error;
    }

    if (isExpectedDecodeMiss(error)) {
        return new ScannerError(
            'DECODE_NO_RESULT',
            'O frame atual não contém um código legível.',
            {
                cause: error,
                context,
                recoverable: false,
                details: {
                    originalName: error?.name || error?.constructor?.name,
                    originalKind: error?.getKind?.() || error?.kind || null,
                    originalMessage: error?.message || null,
                },
            }
        );
    }

    const definition = ERROR_DEFINITIONS[error?.name];
    const fallbackMessage = String(error?.message || '').trim();

    if (definition) {
        return new ScannerError(
            definition.code,
            definition.message,
            {
                cause: error,
                context,
                recoverable: definition.recoverable,
                details: {
                    originalName: error?.name,
                    originalMessage: fallbackMessage || null,
                    constraint: error?.constraint,
                },
            }
        );
    }

    return new ScannerError(
        'UNEXPECTED_ERROR',
        fallbackMessage || 'Ocorreu uma falha inesperada no scanner.',
        {
            cause: error,
            context,
            recoverable: true,
            details: {
                originalName: error?.name || error?.constructor?.name,
                originalKind: error?.getKind?.() || error?.kind || null,
                originalMessage: fallbackMessage || null,
            },
        }
    );
}

export function serializeError(error) {
    const normalized = normalizeError(error);

    return {
        name: normalized.name,
        code: normalized.code,
        message: normalized.message,
        recoverable: normalized.recoverable,
        context: normalized.context,
        details: normalized.details,
    };
}

const RECOVERY_CODES = new Set([
    'CAMERA_PERMISSION_DENIED',
    'CAMERA_NOT_READABLE',
    'CAMERA_ABORTED',
    'CAMERA_VIDEO_TRACK_MISSING',
    'CAMERA_PREVIEW_TIMEOUT',
    'CAMERA_PREVIEW_ERROR',
    'CAMERA_OPEN_FAILED',
]);

export function isRecoveryEligible(error) {
    const normalized = normalizeError(error);

    if (isExpectedDecodeMiss(normalized)) {
        return false;
    }

    return (
        RECOVERY_CODES.has(normalized.code)
        || normalized.context === 'camera.track'
    );
}
